import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';

const initialState = {};

export const nodeSliceV2 = createSlice({
	name: 'nodeV2State',
	initialState,
	reducers: {},
});

/**
 * Create Node - POST /v2/create
 * @param {object} req_data - Node data including project_id, node_type, node_name, node_address, coordinates, POC details
 * @param {function} setErrors - Error setter for form validation
 * @returns {Promise} API response
 */
export const createNode =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			create_type: 'node',
			data: {
				project_id: req_data?.project_id,
				node_type: req_data?.node_type,
				node_name: req_data?.node_name,
				node_address: req_data?.node_address,
				latitude: req_data?.latitude,
				longitude: req_data?.longitude,
				pincode: req_data?.pincode,
				poc_name: req_data?.poc_name,
				poc_mobile_number: req_data?.poc_mobile_number,
				poc_email: req_data?.poc_email,
			},
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Node created successfully');
			return res;
		});
	};

/**
 * Update Node - POST /v2/update
 * @param {object} req_data - Node data to update (id required, other fields optional)
 * @param {function} setErrors - Error setter for form validation
 * @returns {Promise} API response
 */
export const updateNode =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			update_type: 'node',
			data: {
				id: req_data?.id,
				...(req_data?.node_name && { node_name: req_data.node_name }),
				...(req_data?.node_address && { node_address: req_data.node_address }),
				...(req_data?.latitude !== undefined && {
					latitude:
						typeof req_data.latitude === 'string'
							? parseFloat(req_data.latitude)
							: req_data.latitude,
				}),
				...(req_data?.longitude !== undefined && {
					longitude:
						typeof req_data.longitude === 'string'
							? parseFloat(req_data.longitude)
							: req_data.longitude,
				}),
				...(req_data?.pincode && { pincode: req_data.pincode }),
				...(req_data?.poc_name && { poc_name: req_data.poc_name }),
				...(req_data?.poc_mobile_number && {
					poc_mobile_number: req_data.poc_mobile_number,
				}),
				...(req_data?.poc_email && { poc_email: req_data.poc_email }),
				...(req_data?.is_active !== undefined && { is_active: req_data.is_active }),
				...(req_data?.node_type && { node_type: req_data.node_type }),
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Node updated successfully');
			return res;
		});
	};

/**
 * Search Nodes - POST /v2/search
 * @param {object} filter_by - Filter criteria (node_name, node_type, project_id, client_id, city, state, pincode, etc.)
 * @param {object} pagination - Pagination settings (page, per_page)
 * @param {object} sort_by - Sort settings (field, order)
 * @returns {Promise} API response with nodes list
 */
export const searchNodes =
	({ filter_by = {}, pagination = {}, sort_by = {} }) =>
	async () => {
		const data = {
			search_type: 'node',
			...(Object.keys(filter_by).length > 0 && { filter_by }),
			...(Object.keys(pagination).length > 0 && { pagination }),
			...(Object.keys(sort_by).length > 0 && { sort_by }),
		};

		const CREDENTIALS = {
			url: '/search',
			data,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => response);
	};

/**
 * Get Node Details - POST /v2/search
 * @param {string} node_id - Node ID to fetch details for
 * @returns {Promise} API response with complete node details and associated projects
 */
export const getNodeDetails =
	({ node_id }) =>
	async () => {
		const data = {
			search_type: 'node_details',
			filter_by: {
				node_id,
			},
		};

		const CREDENTIALS = {
			url: '/search',
			data,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => response);
	};

/**
 * Remove Node - POST /v2/update
 * @param {string} node_id - Node ID to remove
 * @param {string} removal_reason - Reason for removal (required)
 * @param {function} setErrors - Error setter for form validation
 * @returns {Promise} API response
 */
export const removeNode =
	({ node_id, removal_reason, setErrors }) =>
	async () => {
		const data = {
			update_type: 'remove_node',
			data: {
				node_id,
				removal_reason,
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Node removed successfully');
			return res;
		});
	};

/**
 * Export Nodes CSV - POST /v2/report
 * @param {object} filter_by - Filter criteria for export (node_name, node_type, city, is_active, etc.)
 * @returns {Promise} API response with download URL
 */
export const exportNodeCSVV2 =
	({ filter_by = {} }) =>
	async () => {
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: '/report',
			data: {
				report_type: 'node_export',
				format: 'csv',
				data: { filter_by: cleanFilterBy },
			},
			version: 'v2',
		};

		const response = await API.common(CREDENTIALS);
		const downloadUrl = response?.data?.download_url || response?.download_url;
		if (downloadUrl) {
			window.open(downloadUrl, '_blank');
		}
		return response;
	};

export default nodeSliceV2.reducer;
